import { Box, Typography, TextField, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';

export const StyledContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: "1 1 auto",
  overflowY: 'auto',
  overflowX: 'hidden',
  height: '100%',
  color: '#2c3e50',
  padding: theme.spacing(3),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 20%, rgba(255, 102, 0, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(15, 40, 76, 0.03) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
}));

export const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(6),
  position: 'relative',
  zIndex: 1,
}));

export const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(6),
  animation: 'fadeInUp 0.8s ease-out',
  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateY(30px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

export const LogoImage = styled('img')(({ theme }) => ({
  width: '72px',
  height: '72px',
  filter: 'drop-shadow(0 4px 12px rgba(15, 40, 76, 0.15))',
}));

export const MainTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
  fontWeight: 700,
  margin: 0,
  color: theme.palette.secondary.main,
  letterSpacing: '-0.02em',
  lineHeight: 1.1,
}));

export const SubTitle = styled(Typography)(({ theme }) => ({
  color: '#64748b',
  marginBottom: theme.spacing(6),
  fontSize: '1.1rem',
  fontWeight: 400,
  textAlign: 'center',
  maxWidth: 480,
  lineHeight: 1.6,
  animation: 'fadeInUp 0.8s ease-out 0.2s both',
  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

export const SearchContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '600px',
  margin: '0 auto',
  marginBottom: theme.spacing(8),
  animation: 'fadeInUp 0.8s ease-out 0.4s both',
  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderRadius: theme.spacing(3),
    fontSize: '1.1rem',
    padding: theme.spacing(0.5, 2),
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(15, 40, 76, 0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderColor: 'rgba(15, 40, 76, 0.2)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 20px rgba(15, 40, 76, 0.08)',
    },
    '&.Mui-focused': {
      backgroundColor: 'white',
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
    },
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(2, 1),
    fontSize: '1.1rem',
    '&::placeholder': {
      color: '#94a3b8',
      opacity: 1,
    },
  },
}));

export const PromptsSection = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1000px',
  margin: '0 auto',
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.3rem',
  fontWeight: 600,
  color: theme.palette.secondary.main,
  marginBottom: theme.spacing(3),
  textAlign: 'center',
}));

export const CategoryChipsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
  animation: 'fadeInUp 0.8s ease-out 0.6s both',
  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

export const CategoryChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  backgroundColor: isSelected ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.8)',
  color: isSelected ? 'white' : '#64748b',
  fontWeight: isSelected ? 600 : 500,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(0.5, 1),
  border: `1px solid ${isSelected ? theme.palette.primary.main : 'rgba(15, 40, 76, 0.1)'}`,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: isSelected ? theme.palette.primary.dark : 'rgba(255, 255, 255, 0.95)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(15, 40, 76, 0.1)',
    },
}));

export const PromptsGrid = styled(Box)(({ theme }) => ({
  animation: 'fadeInUp 0.8s ease-out 0.8s both',
  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

export const PromptCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(15, 40, 76, 0.12)',
  borderColor: 'rgba(19, 110, 237, 0.12)',
  borderRadius: theme.spacing(3),
  cursor: 'pointer',
  transition: 'all 3s ease',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  minHeight: '60px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderColor: theme.palette.primary.main,
    // transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(15, 40, 76, 0.1)',
  },
  '&:active': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
}));

export const PromptContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: theme.spacing(0.5),
}));

export const PromptIcon = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: `${theme.palette.primary.main}12`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    fontSize: 20,
    color: theme.palette.primary.main,
  },
}));

export const PromptTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.95rem',
  fontWeight: 500,
  color: theme.palette.secondary.main,
  lineHeight: 1.3,
  margin: 0,
}));

export const PromptSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.8rem',
  color: '#64748b',
  fontWeight: 400,
  lineHeight: 1.4,
  margin: 0,
}));
